'use client';

import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Icon from '@/components/ui/AppIcon';
import LottieRender from './LottieRender';

const capabilities = [
    { title: "Full-Stack Dev", desc: "React, Next.js, Node.js architecture.", icon: "CodeBracketIcon", color: "bg-blue-500" },
    { title: "Cloud Infra", desc: "AWS, Scalable serverless systems.", icon: "CloudIcon", color: "bg-cyan-500" },
    { title: "SEO Strategy", desc: "Technical optimization & visibility.", icon: "ChartBarIcon", color: "bg-indigo-500" },
    { title: "UX/UI Design", desc: "Human-centric digital experiences.", icon: "PaintBrushIcon", color: "bg-violet-500" }
];

const MobileUnifiedCapabilities = () => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: false,
        align: 'center',
        containScroll: 'trimSnaps'
    });

    const [selectedIndex, setSelectedIndex] = React.useState(0);

    React.useEffect(() => {
        if (!emblaApi) return;

        const onSelect = () => {
            setSelectedIndex(emblaApi.selectedScrollSnap());
        };

        emblaApi.on('select', onSelect);
        onSelect(); // Initial load

        return () => {
            emblaApi.off('select', onSelect);
        }
    }, [emblaApi]);

    return (
        <section className="py-20 bg-slate-50 overflow-hidden">
            <div className="px-6 mb-8 text-center">
                <h2 className="text-3xl font-black text-slate-900 mb-2">
                    Unified <span className="text-indigo-600">Capabilities</span>
                </h2>
                <p className="text-sm text-slate-500 font-medium">
                    Swipe to explore our syllabus of excellence.
                </p>
            </div>

            {/* Animation Container */}
            <div className="mx-6 mb-10 h-48 bg-white rounded-[2rem] shadow-sm border border-slate-100 flex items-center justify-center p-4 overflow-hidden relative">
                <div className="absolute inset-0 bg-indigo-50/50" />
                <div className="w-full h-full relative z-10">
                    <LottieRender animationPath="/animations/team_v2.json" />
                </div>
            </div>

            {/* Carousel */}
            <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex touch-pan-y pl-6 pr-12 gap-4 py-8">
                    {capabilities.map((cap, i) => {
                        const isSelected = i === selectedIndex;
                        return (
                            <div key={i} className="flex-[0_0_80%] min-w-0 transition-transform duration-300 ease-out" style={{ transform: isSelected ? 'scale(1)' : 'scale(0.92)' }}>
                                <div className={`bg-white rounded-[2rem] p-6 shadow-xl shadow-slate-200/50 border border-slate-100 h-full flex flex-col relative overflow-hidden group transition-opacity duration-300 ${isSelected ? 'opacity-100 ring-2 ring-indigo-50' : 'opacity-60'}`}>
                                    {/* Decor Blob */}
                                    <div className={`absolute top-0 right-0 w-32 h-32 ${cap.color} opacity-10 rounded-full blur-3xl -mr-10 -mt-10`} />

                                    <div className="relative z-10">
                                        <div className="flex justify-between items-start mb-6">
                                            <div className={`w-14 h-14 rounded-2xl ${cap.color} flex items-center justify-center text-white shadow-lg`}>
                                                <Icon name={cap.icon} className="w-7 h-7" />
                                            </div>
                                            <span className="text-4xl font-black text-slate-100 select-none">
                                                0{i + 1}
                                            </span>
                                        </div>

                                        <h3 className="text-2xl font-bold text-slate-900 mb-3 leading-tight">
                                            {cap.title}
                                        </h3>
                                        <p className="text-slate-600 font-medium leading-relaxed">
                                            {cap.desc}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-2 mt-2 pb-8">
                {capabilities.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => emblaApi && emblaApi.scrollTo(index)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${index === selectedIndex
                                ? 'w-6 bg-indigo-600'
                                : 'w-1.5 bg-slate-300'
                            }`}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>
        </section>
    );
};

export default MobileUnifiedCapabilities;
